import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Calendar, MessageSquare, Check } from "lucide-react";

interface MailingHistory {
  id: string;
  campaign_name: string | null;
  mailed_date: string | null;
  mailing_round: number;
  letter_type: string | null;
  returned: boolean;
  response_received: boolean;
  response_date: string | null;
  response_notes: string | null;
}

interface Props {
  history: MailingHistory[];
  onResponseSaved: () => void;
}

export function MailingHistoryTimeline({ history, onResponseSaved }: Props) {
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [responseDate, setResponseDate] = useState("");
  const [responseNotes, setResponseNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSaveResponse = async (historyId: string) => {
    setSaving(true);
    const { error } = await supabase
      .from("mailing_history")
      .update({
        response_received: true,
        response_date: responseDate || null,
        response_notes: responseNotes || null,
      })
      .eq("id", historyId);

    setSaving(false);
    if (error) {
      toast.error("Failed to save response");
    } else {
      toast.success("Response recorded");
      setRespondingTo(null);
      setResponseDate("");
      setResponseNotes("");
      onResponseSaved();
    }
  };

  const openResponseForm = (h: MailingHistory) => {
    setRespondingTo(h.id);
    setResponseDate(h.response_date || new Date().toISOString().split("T")[0]);
    setResponseNotes(h.response_notes || "");
  };

  return (
    <div>
      <h4 className="font-medium mb-3 flex items-center gap-2">
        <Calendar className="h-4 w-4" />
        Mailing History
      </h4>
      {history.length === 0 ? (
        <p className="text-sm text-muted-foreground">No mailings recorded</p>
      ) : (
        <div className="space-y-3">
          {history.map((h) => (
            <div key={h.id} className="p-3 bg-muted/50 rounded-lg space-y-2">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                  {h.mailing_round}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{h.campaign_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {h.mailed_date || "No date"} · {h.letter_type}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {h.returned && <Badge variant="destructive">Returned</Badge>}
                  {h.response_received && (
                    <Badge variant="default" className="cursor-pointer" onClick={() => openResponseForm(h)}>
                      <Check className="h-3 w-3 mr-1" /> {h.response_date || "Response"}
                    </Badge>
                  )}
                  {!h.response_received && !h.returned && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openResponseForm(h)}>
                      <MessageSquare className="h-3 w-3 mr-1" /> Log Response
                    </Button>
                  )}
                </div>
              </div>

              {/* Response notes display */}
              {h.response_received && h.response_notes && respondingTo !== h.id && (
                <p className="text-xs text-muted-foreground ml-11 italic">"{h.response_notes}"</p>
              )}

              {/* Inline response form */}
              {respondingTo === h.id && (
                <div className="ml-11 space-y-2 pt-1 border-t border-border/50">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">Response Date</p>
                      <Input
                        type="date"
                        value={responseDate}
                        onChange={(e) => setResponseDate(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <Textarea
                      value={responseNotes}
                      onChange={(e) => setResponseNotes(e.target.value)}
                      placeholder="What did they say?"
                      className="text-sm min-h-[50px]"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs" onClick={() => handleSaveResponse(h.id)} disabled={saving}>
                      {saving ? "Saving..." : "Save Response"}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setRespondingTo(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
